// src/components/filings/TickerFilingsSummary.jsx
// SEC-FILINGS-PAGE — new file. Replaces the earlier FilingFilters+FilingsFeed
// clone on the StockPage.jsx tab with the actual confirmed design.

import React from 'react';
import { useTickerFilingsSummary } from '../../hooks/useTickerFilingsSummary';
import { significanceWeight, LOCK_COPY } from '../../data/filingFieldRegistry';

const EDGAR_BASE = 'https://www.sec.gov/cgi-bin/browse-edgar';

function edgarSearchUrl(cik, formType) {
  const params = new URLSearchParams({
    action: 'getcompany',
    CIK: cik,
    type: formType || '',
    owner: 'include',
    count: '40',
  });
  return EDGAR_BASE + '?' + params.toString();
}

function GroupRow({ filing, onNavigate }) {
  const weight = significanceWeight(filing.significance);
  if (filing.locked) {
    const copy = LOCK_COPY[filing.lockReason] || LOCK_COPY.SIGN_IN_REQUIRED;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0',
        borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: 0.6 }}>
        <span style={{ fontSize: '0.8rem', color: '#475569', flex: 1 }}>🔒 {copy.title}</span>
        <span style={{ fontSize: '0.75rem', color: '#374151' }}>{filing.filedAt}</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: '0.8rem', color: '#cbd5e1', flex: '0 0 90px' }}>{filing.filingType}</span>
      <span style={{ fontSize: '0.75rem', color: '#64748b', flex: 1 }}>{filing.filedAt}</span>
      <div title={`Significance ${filing.significance}/10`} style={{ display: 'flex', gap: '1px' }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} style={{ width: 3, height: 10, borderRadius: '1px',
            background: i < weight.filled ? (weight.isHighConviction ? '#ff4444' : '#00d4ff') : 'rgba(255,255,255,0.08)' }} />
        ))}
      </div>
      {filing.secUrl && (
        <a href={filing.secUrl} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '0.72rem', fontWeight: 700, color: '#00ff88', textDecoration: 'none' }}>
          View ↗
        </a>
      )}
    </div>
  );
}

function FilingGroup({ group, cik, onNavigate }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.3rem' }}>
        {group.label}
      </div>
      {group.filings.map((f, i) => <GroupRow key={f.filedAt + i} filing={f} onNavigate={onNavigate} />)}
      {cik && (
        <a href={edgarSearchUrl(cik, group.formTypeForSearch)} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: '0.4rem', fontSize: '0.72rem',
            color: '#00d4ff', textDecoration: 'none' }}>
          EDGAR Direct Search: {group.formTypeForSearch} — Older Forms →
        </a>
      )}
    </div>
  );
}

export default function TickerFilingsSummary({ ticker, token, onNavigate }) {
  const { cik, groups, loading, error } = useTickerFilingsSummary(ticker, token);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading filings…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4444' }}>{error}</div>;

  return (
    <div>
      {/* Moved to top per product decision — quick access, not buried below a
          potentially long grouped list. Disclaimer stays generic — never
          states the actual per-form-type capture limits (competitive
          mechanics), just that this isn't the complete SEC record. */}
      {cik ? (
        <div style={{ marginBottom: '1.25rem' }}>
          <a href={edgarSearchUrl(cik, '')} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.5rem 1rem', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
              borderRadius: '8px', color: '#00d4ff', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 700 }}>
            View all SEC filings on EDGAR →
          </a>
          <div style={{ fontSize: '0.68rem', color: '#475569', marginTop: '0.4rem' }}>
            * Showing our indexed filings, sorted by significance — not the complete SEC filing history for this company.
          </div>
        </div>
      ) : (
        <div style={{ fontSize: '0.72rem', color: '#374151', marginBottom: '1.25rem' }}>
          No SEC CIK on record for this ticker.
        </div>
      )}

      {groups.length === 0 && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#475569', fontSize: '0.85rem' }}>
          No filings indexed yet
        </div>
      )}

      {groups.map(g => <FilingGroup key={g.label} group={g} cik={cik} onNavigate={onNavigate} />)}
    </div>
  );
}
